import { zeroAddress } from 'viem'
import type { MidnightMarketConfig } from '@1delta/data-sdk'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import type { FixedTermInfo } from '../../../types/lenderTypes'
import { midnightCollateralRef } from '@1delta/data-sdk'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import {
  parseLtv,
  liquidationPenaltyFromLltv,
  formatNr,
} from '../morpho/utils/parsers'
import {
  tickToAprNumber,
  midnightLiquidationPenaltyNumber,
  midnightSettlementFeeForTtm,
  midnightContinuousFeeAprPercent,
} from './math'
import type { MidnightMarketRaw, MidnightBookLevel } from './types'

const nowSec = () => Math.floor(Date.now() / 1000)

/** Parse a WAD string/bigint into a bigint, defaulting to 0n on bad input. */
function toBigIntOr0(v: string | bigint | number | undefined | null): bigint {
  if (v === undefined || v === null || v === '') return 0n
  try {
    return typeof v === 'bigint' ? v : BigInt(v)
  } catch {
    return 0n
  }
}

/** Synthesized per-market lender key, e.g. `MORPHO_MIDNIGHT_<MARKETID_HEX_UPPER>`. */
export function midnightLenderKey(marketId: string): string {
  return 'MORPHO_MIDNIGHT_' + marketId.slice(2).toUpperCase()
}

function currencyFor(
  address: string,
  decimals: number,
  tokens: GenericTokenList,
): any {
  const lower = address.toLowerCase()
  return (
    tokens[lower] ?? {
      address: lower,
      symbol: '',
      name: '',
      decimals,
    }
  )
}

/**
 * Map fetched Midnight markets into the shared `MorphoGeneralPublicResponse`
 * shape used by every collateralized-borrow lender, keyed by the synthesized
 * `MORPHO_MIDNIGHT_<id>` lender key.
 *
 * One LOAN entry per market carries the fixed rates:
 *   - `variableBorrowRate` = best fixed BORROW APR (from bids), in percent
 *   - `depositRate`        = best fixed SUPPLY/lend APR (from asks), in percent
 * plus order-book depth as the "liquidity" proxy. One COLLATERAL entry per
 * collateral leg carries the per-leg LLTV → collateralFactor + liquidation
 * penalty. `params.market` carries the full Midnight market struct + maturity.
 */
export function convertMidnightMarketsToResponse(
  raw: MidnightMarketRaw[],
  chainId: string,
  prices: { [asset: string]: number } = {},
  _additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}
  const now = nowSec()

  for (const { config, top, book } of raw) {
    if (!config?.marketId || !config.loanToken) continue

    const m = midnightLenderKey(config.marketId)
    const maturity = Number(config.maturity)
    const ttm = maturity - now
    const matured = ttm <= 0
    const ttmBig = BigInt(Math.max(0, ttm))

    const supplyAprPct =
      top?.bestSupplyTick != null && !matured
        ? tickToAprNumber(top.bestSupplyTick, ttmBig) * 100
        : 0
    const borrowAprPct =
      top?.bestBorrowTick != null && !matured
        ? tickToAprNumber(top.bestBorrowTick, ttmBig) * 100
        : 0

    // Mutable per-market fees, snapshotted on-chain into the market config (see
    // MidnightMarketConfig). Effective settlement fee is interpolated at the
    // market's current time-to-maturity; continuous fee is annualized to a
    // percent (a haircut on the lender's realized supply yield).
    const settlementFee = midnightSettlementFeeForTtm(
      config.settlementFeeCbp,
      ttm,
    )
    const continuousFeeAprPct = midnightContinuousFeeAprPercent(
      config.continuousFee,
    )

    // Fixed-term rate card: each Midnight market has exactly ONE maturity, so
    // it maps to a single-term product (durationDays to maturity, apr = the
    // fixed borrow rate). Consumed generically by fixed-term-aware pipelines
    // (e.g. yield-tracer's `market_terms`), same shape as Lista broker terms.
    // Only emitted when there's a live borrowable rate.
    const terms =
      borrowAprPct > 0 && !matured
        ? [
            {
              termId: 0,
              durationSecs: Math.max(0, ttm),
              durationDays: ttm / 86400,
              apr: borrowAprPct,
            },
          ]
        : undefined

    const loanAddr = config.loanToken.toLowerCase()
    // Price map is keyed by asset group (e.g. "USDC") with a "chainId-address"
    // fallback. Use `||` (not `??`): `toOracleKey(undefined)` returns `''`, so
    // `??` would never reach the fallback and every asset-group-less loan token
    // would resolve to `''` → price 0. The fallback keys off `loanAddr` (already
    // lowercased) to match the flattened map's `${chainId}-${address}` entries.
    const loanAsset = tokens[loanAddr]
    const loanKey =
      toOracleKey(loanAsset?.assetGroup!) ||
      toGenericPriceKey(loanAddr, chainId)
    const loanPrice = prices[loanKey] ?? 0
    const supplyLiquidity = formatNr(
      top?.supplyDepthAssets ?? 0n,
      config.loanDecimals,
    )
    const borrowLiquidity = formatNr(
      top?.borrowDepthAssets ?? 0n,
      config.loanDecimals,
    )

    // @ts-ignore — built incrementally, matches Morpho converter pattern
    const entry: MorphoGeneralPublicResponse = { data: {} }

    // 1. LOAN entry (the borrow/lend market)
    const loanUid = createMarketUid(chainId, m, loanAddr)
    entry.data[loanUid] = {
      marketUid: loanUid,
      name: 'Loan ' + (tokens[loanAddr]?.symbol ?? ''),
      poolId: loanAddr,
      underlying: loanAddr,
      asset: currencyFor(loanAddr, config.loanDecimals, tokens),
      totalDeposits: supplyLiquidity,
      totalDebtStable: 0,
      totalDebt: 0,
      totalLiquidity: borrowLiquidity,
      borrowLiquidity,
      totalLiquidityUSD: borrowLiquidity * loanPrice,
      borrowLiquidityUSD: borrowLiquidity * loanPrice,
      totalDepositsUSD: supplyLiquidity * loanPrice,
      totalDebtStableUSD: 0,
      totalDebtUSD: 0,
      utilization: 0,
      depositRate: supplyAprPct,
      variableBorrowRate: borrowAprPct,
      rateModel: 'fixedTerm',
      // Midnight is fixed-rate: expose the fixed borrow APR on stableBorrowRate
      // too, so fixed-rate consumers pick it up like a term product.
      stableBorrowRate: borrowAprPct,
      // The rate card is a BORROW-side fact, so it lives on the row that has a
      // borrow side — natively, not by a consumer matching `loanAddress`
      // against each row's asset. That match is what broke when the loan token
      // was also a collateral leg: the leg row carries the same token, so it
      // matched too and was served a "Fixed from X %" it cannot offer.
      // `params.market.terms` still carries the same card for readers of the
      // market-level shape; the collateral legs below never carry one.
      terms,
      intrinsicYield: 0,
      rewards: undefined,
      decimals: config.loanDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: 0,
          collateralFactor: 0,
          borrowFactor: 1,
          liquidationPenalty: 0,
          closeFactor: 1,
          collateralDisabled: true,
          debtDisabled: false,
        },
      },
      closeFactor: 1,
      collateralActive: false,
      borrowingEnabled: !matured,
      depositsEnabled: !matured,
      hasStable: false,
      // Midnight borrowing is fixed-term (carried via `terms` + `maturity` on
      // params.market), but it IS borrowable — do NOT mark variable borrow
      // disabled, or borrowable-only filters would hide the market.
      variableBorrowDisabled: false,
      isActive: true,
      isFrozen: false,
    } as any

    // 2. COLLATERAL entries — one per collateral leg (index = on-chain collateralIndex)
    config.collateralParams.forEach((c, i) => {
      const collAddr = c.token.toLowerCase()
      const ltv = parseLtv(c.lltv)
      // Midnight prices the liquidation penalty off the per-collateral
      // `liquidationCursor` (uncapped `maxLif`), not the fixed 0.3 cursor / 1.15
      // cap Morpho assumes. Fall back to the LLTV approximation only when the
      // cursor is missing/zero (legacy config), so we never regress to 0.
      const cursor = toBigIntOr0(c.liquidationCursor)
      const liquidationPenalty =
        cursor > 0n
          ? midnightLiquidationPenaltyNumber(toBigIntOr0(c.lltv), cursor)
          : liquidationPenaltyFromLltv(ltv)
      // Leg-keyed, never asset-keyed: see `midnightCollateralRef`. A leg whose
      // token IS the loan token (Tenor's ~98% vault-as-collateral shape) would
      // otherwise share the loan row's uid and overwrite it.
      const collUid = createMarketUid(chainId, m, midnightCollateralRef(collAddr, i))
      // Order-book collateral has no supply pool/cap — the binding constraint is
      // the loan-side BORROW depth. Surface a collateral capacity backed by that
      // depth (borrow depth grossed up by LTV, since you post collateral and
      // borrow up to LTV against it) so real Midnight pairs aren't hidden by the
      // optimizer's collateral-liquidity floor. Stays 0 when the book is empty,
      // so empty-maturity markets are still (correctly) filtered out.
      const collAsset = tokens[collAddr]
      const collKey =
        toOracleKey(collAsset?.assetGroup!) || toGenericPriceKey(collAddr, chainId)
      const collPrice = prices[collKey] ?? 0
      const collCapacityUSD =
        ltv > 0 ? (borrowLiquidity * loanPrice) / ltv : borrowLiquidity * loanPrice
      const collCapacity = collPrice > 0 ? collCapacityUSD / collPrice : 0

      entry.data[collUid] = {
        marketUid: collUid,
        name: 'Collateral ' + (tokens[collAddr]?.symbol ?? ''),
        poolId: collAddr,
        underlying: collAddr,
        asset: currencyFor(collAddr, c.decimals, tokens),
        totalDeposits: collCapacity,
        totalDebtStable: 0,
        totalDebt: 0,
        totalLiquidity: collCapacity,
        borrowLiquidity: 0,
        totalLiquidityUSD: collCapacityUSD,
        borrowLiquidityUSD: 0,
        totalDepositsUSD: collCapacityUSD,
        totalDebtStableUSD: 0,
        totalDebtUSD: 0,
        utilization: 0,
        depositRate: 0,
        variableBorrowRate: 0,
        stableBorrowRate: 0,
        intrinsicYield: 0,
        rewards: undefined,
        decimals: c.decimals,
        config: {
          0: {
            category: 0,
            borrowCollateralFactor: ltv,
            collateralFactor: ltv,
            borrowFactor: 1,
            liquidationPenalty,
            closeFactor: 1,
            collateralDisabled: false,
            debtDisabled: true,
          },
        },
        closeFactor: 1,
        collateralActive: true,
        borrowingEnabled: false,
        depositsEnabled: !matured,
        hasStable: false,
        isActive: true,
        isFrozen: false,
      } as any
    })

    // Bounded book chunk (best-first, top-N levels/side) → provider-agnostic
    // rate+size levels for downstream filtering. Per-level APR from the tick +
    // current TTM. `bids` = borrow side, `asks` = lend side (Midnight semantics).
    const toLevels = (levels?: MidnightBookLevel[]) =>
      (levels ?? []).map((l) => ({
        apr: tickToAprNumber(l.tick, ttmBig) * 100,
        units: l.units.toString(),
        assets: formatNr(l.assets, config.loanDecimals),
      }))
    const marketBook =
      book && !matured
        ? { bids: toLevels(book.bids), asks: toLevels(book.asks) }
        : undefined

    const primaryCollateral = config.collateralParams[0]
    entry.params = {
      market: {
        lender: m,
        name: config.name,
        loanDecimals: config.loanDecimals,
        collateralDecimals: primaryCollateral?.decimals,
        id: config.marketId,
        lltv: primaryCollateral?.lltv ?? '0',
        oracle: primaryCollateral?.oracle ?? zeroAddress,
        irm: zeroAddress,
        collateralAddress: primaryCollateral?.token ?? zeroAddress,
        loanAddress: loanAddr,
        // Fixed-term rate card (single maturity); undefined when no live rate.
        terms,
        // Midnight-specific extras (consumed by calldata / worker-api resolvers)
        maturity: config.maturity,
        rcfThreshold: config.rcfThreshold,
        // Raw on-chain-snapshotted fees — authoritative for downstream ingest
        // (yield-tracer reads these verbatim).
        settlementFeeCbp: config.settlementFeeCbp,
        continuousFee: config.continuousFee,
        // Canonical cross-protocol fixed-term descriptor (Lista + Midnight share
        // this shape). Midnight: single calendar maturity, continuous +
        // settlement fees, and NO early-repay penalty (exit = buy debt units
        // back on the book at market price).
        fixedTerm: {
          model: 'midnight',
          maturity: Number(config.maturity),
          fees: {
            continuousFeeApr: continuousFeeAprPct,
            settlementFee,
          },
          earlyRepay: { kind: 'none' },
          // Midnight terms are an aggregate of many signed maker offers — there
          // is no single provider at the market level (the concrete maker(s) are
          // per-offer, surfaced at quote time), so mark it an order book.
          provider: { kind: 'orderbook' },
        } as FixedTermInfo,
        enterGate: config.enterGate,
        liquidatorGate: config.liquidatorGate,
        collateralParams: config.collateralParams,
        // Bounded top-of-book slice (top-N levels/side) for later filtering.
        book: marketBook,
      } as any,
    }
    entry.chainId = chainId

    out[m] = entry
  }

  return out
}

export type { MidnightMarketConfig }
